// Fetches the canonical persisted transcript for the active session and
// mirrors it into the Zustand store via `hydrateMessages`. Legacy uses
// the same pattern (RTK Query + a setLocalMessages mirror inside
// ChatPanel) — V1 owns the same shape with TanStack Query.
//
// The fetcher runs:
//   • Once on session open (TanStack auto-fires on mount).
//   • Again whenever `useAgentForgeStream` invalidates the messages
//     query on `stream_done` / `agent_message` / `stream_error`.
//   • Every TURN_POLL_MS while a turn is in flight — the WS is the fast
//     path, not the only path. If the socket is down/reconnecting for the
//     whole turn (stale token dance on first load, proxy hiccup), the
//     poll is what surfaces the reply instead of indefinite dead air.
//
// Mount this exactly once (in the `AgentForge` umbrella) so every
// surface reads the same Zustand mirror.

import { useEffect } from 'react';
import {
  CANONICAL_TRANSCRIPT_QUERY,
  toStoreInputs,
  useMessagesQuery,
} from '../api/messages';
import type { ServerMessage } from '../api/messages.types';
import { useAgentForgeStore } from '../stores';

// Poll cadence while a turn is in flight. Bounded: polling stops the moment
// the tab leaves 'streaming', so the steady-state (no active turn) is zero
// requests — same as before.
const TURN_POLL_MS = 5_000;
const LEGACY_TERMINAL_CLOCK_SKEW_MS = 5 * 60_000;

// The server transcript's terminal shape for a finished turn: closing
// assistant prose, or an interaction card (clarify/plan) awaiting the user.
// Mid-turn snapshots end with tool results or empty assistant tool-callers —
// those must NOT hydrate (they'd wipe the live draft and drop blocks).
const lastTerminalServerMessage = (
  data: ServerMessage[]
): ServerMessage | undefined => {
  const last = data[data.length - 1];
  if (!last) return undefined;
  if (last.role === 'assistant') return last.content?.trim() ? last : undefined;
  if (last.role === 'tool') {
    const toolName = (last.metadata as { tool_name?: unknown } | undefined)
      ?.tool_name;
    return typeof toolName === 'string' && toolName.startsWith('propose_')
      ? last
      : undefined;
  }
  return undefined;
};

const terminalBelongsToTurn = (
  terminal: ServerMessage,
  workflowId: string,
  data: ServerMessage[],
  turn: {
    phase: string;
    startedAt: string;
    updatedAt?: string;
    authoritativeCompletionWorkflowId?: string;
  }
): boolean => {
  const turnId = (terminal.metadata as { turn_id?: unknown } | undefined)
    ?.turn_id;
  if (typeof turnId === 'string') return turnId === workflowId;

  // During the mixed-fleet rollout, legacy workers persist the terminal
  // assistant row without metadata.turn_id. Only accept that weaker shape
  // after the authoritative status endpoint has put this exact active turn
  // into finalizing, and only once the newest server user/terminal pair falls
  // inside this turn's time window. Running turns still require an exact
  // turn_id, so an older assistant row can never end live work.
  if (
    turn.phase !== 'finalizing' ||
    turn.authoritativeCompletionWorkflowId !== workflowId
  )
    return false;
  const serverUsers = data.filter((message) => message.role === 'user');
  if (serverUsers.length === 0) return false;

  const latestServerUser = serverUsers[serverUsers.length - 1];
  const startedAt = Date.parse(turn.startedAt);
  const latestServerUserAt = Date.parse(latestServerUser.created_at);
  const terminalAt = Date.parse(terminal.created_at);
  if (
    !Number.isFinite(startedAt) ||
    !Number.isFinite(latestServerUserAt) ||
    !Number.isFinite(terminalAt)
  )
    return false;
  if (latestServerUserAt < startedAt - LEGACY_TERMINAL_CLOCK_SKEW_MS)
    return false;
  if (terminalAt < latestServerUserAt) return false;

  const completedAt = turn.updatedAt ? Date.parse(turn.updatedAt) : NaN;
  if (
    Number.isFinite(completedAt) &&
    terminalAt > completedAt + LEGACY_TERMINAL_CLOCK_SKEW_MS
  )
    return false;
  return true;
};

const serverMirrorsOptimisticUser = (
  data: ServerMessage[],
  optimistic: {
    content: string;
    createdAt: string;
    attachments?: Array<{ filename: string }>;
  },
  workflowId: string | undefined,
  startedAt: string | undefined
): boolean => {
  const serverUsers = data.filter((message) => message.role === 'user');
  if (
    workflowId &&
    serverUsers.some(
      (message) =>
        (message.metadata as { turn_id?: unknown } | undefined)?.turn_id ===
        workflowId
    )
  )
    return true;

  const latest = serverUsers[serverUsers.length - 1];
  if (!latest) return false;
  const serverAt = Date.parse(latest.created_at);
  const localAt = Date.parse(optimistic.createdAt);
  const turnStartedAt = startedAt ? Date.parse(startedAt) : localAt;
  if (
    !Number.isFinite(serverAt) ||
    !Number.isFinite(turnStartedAt) ||
    serverAt < turnStartedAt - LEGACY_TERMINAL_CLOCK_SKEW_MS
  )
    return false;

  if (
    optimistic.content.trim().length > 0 &&
    latest.content.trim() === optimistic.content.trim()
  )
    return true;

  const localFiles = (optimistic.attachments ?? [])
    .map((attachment) => attachment.filename)
    .sort();
  const rawServerAttachments = (
    latest.metadata as { attachments?: unknown } | undefined
  )?.attachments;
  const serverFiles = Array.isArray(rawServerAttachments)
    ? rawServerAttachments
        .flatMap((attachment) => {
          if (!attachment || typeof attachment !== 'object') return [];
          const filename = (attachment as { filename?: unknown }).filename;
          return typeof filename === 'string' ? [filename] : [];
        })
        .sort()
    : [];
  return (
    localFiles.length > 0 &&
    localFiles.length === serverFiles.length &&
    localFiles.every((filename, index) => filename === serverFiles[index])
  );
};

export const useAgentForgeMessages = (
  sessionId: string | null | undefined
): void => {
  const hydrateMessages = useAgentForgeStore((s) => s.hydrateMessages);
  const setTabStatus = useAgentForgeStore((s) => s.setTabStatus);
  const clearStreamingDraft = useAgentForgeStore((s) => s.clearStreamingDraft);
  const resolveActiveTurn = useAgentForgeStore((s) => s.resolveActiveTurn);
  const activeTurn = useAgentForgeStore((s) =>
    sessionId ? s.activeTurnsBySession[sessionId] : undefined
  );
  const tabStatus = useAgentForgeStore(
    (s) => s.openTabs.find((t) => t.sessionId === sessionId)?.status
  );

  const query = useMessagesQuery({
    sessionId,
    // Recovery must inspect the actual terminal turn, not the oldest default
    // page in a long-running chat.
    ...CANONICAL_TRANSCRIPT_QUERY,
    // Local instant-feedback placeholders ('pending-') don't exist
    // server-side — skip the fetch until migrateSession re-keys them.
    enabled: !!sessionId && !sessionId.startsWith('pending-'),
    // In-flight turns poll as a WS-independent fallback; idle sessions
    // don't poll at all (invalidation-driven, same as legacy).
    refetchIntervalMs:
      activeTurn || tabStatus === 'streaming' ? TURN_POLL_MS : undefined,
  });

  useEffect(() => {
    const data = query.data;
    if (!sessionId || !data || data.length === 0) return;

    // Never hydrate a MID-TURN snapshot. The server transcript then is a
    // partial [user, empty_assistant_tool_calls, tool] — folding tool blocks
    // into a missing-yet-final assistant turn drops them on the floor, and
    // `clearStreamingDraft()` below would wipe the live WS draft that *did*
    // have the card. But when the transcript already shows the turn's
    // TERMINAL shape (closing prose / clarify card), the turn is de-facto
    // done even if stream_done never reached us — hydrating is exactly the
    // recovery a dead socket needs.
    const state = useAgentForgeStore.getState();
    const currentTurn = state.activeTurnsBySession[sessionId];
    const streaming =
      !!currentTurn ||
      state.streamingDraft?.sessionId === sessionId ||
      state.openTabs.find((t) => t.sessionId === sessionId)?.status ===
        'streaming';
    const terminal = lastTerminalServerMessage(data);
    const local = state.messagesBySession[sessionId] ?? [];
    if (streaming && !terminal) return;
    if (
      currentTurn &&
      (!currentTurn.workflowId ||
        !terminal ||
        !terminalBelongsToTurn(
          terminal,
          currentTurn.workflowId,
          data,
          currentTurn
        ))
    )
      return;

    // Don't clobber a fresh optimistic user message before the server
    // has caught up. Two gates:
    //   1. Server must have a new turn-closing message we don't know about —
    //      a new assistant turn, or a trailing clarify/plan card (clarify-only
    //      turns end without assistant prose).
    //   2. Server must contain this optimistic turn's exact workflow-tagged
    //      user row (legacy fallback: matching text/attachments in the turn
    //      time window). Absolute counts are invalid for a sliding tail.
    const optimisticUser = [...local]
      .reverse()
      .find(
        (message) => message.role === 'user' && message.id.startsWith('user-')
      );
    if (optimisticUser?.role === 'user') {
      const knownAssistantIds = new Set(
        local.filter((m) => m.role === 'assistant').map((m) => m.id)
      );
      const serverHasNewAssistant = data.some(
        (m) => m.role === 'assistant' && !knownAssistantIds.has(m.id)
      );
      const endsWithClarify =
        data[data.length - 1]?.role === 'tool' && !!terminal;
      if (!serverHasNewAssistant && !endsWithClarify) return;
      if (
        !serverMirrorsOptimisticUser(
          data,
          optimisticUser,
          currentTurn?.workflowId,
          currentTurn?.startedAt
        )
      )
        return;
    }

    hydrateMessages(sessionId, toStoreInputs(data));

    // Drop the WS draft in the same tick as the canonical record lands so
    // the streamed text swaps directly into the persisted message — no
    // "Thinking…" gap in between. `useAgentForgeStream` deliberately
    // leaves the draft alive on `stream_done` for this reason.
    clearStreamingDraft();

    // Flip the tab's status dot off once the canonical record has
    // landed. No-op for inline `/agent` (no tab in the open list).
    if (state.activeTurnsBySession[sessionId]) {
      resolveActiveTurn(sessionId, 'done');
    } else if (tabStatus === 'streaming') {
      setTabStatus(sessionId, 'done');
    }
  }, [
    sessionId,
    query.data,
    hydrateMessages,
    clearStreamingDraft,
    resolveActiveTurn,
    setTabStatus,
    tabStatus,
    activeTurn,
  ]);
};
